import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import type {DibDocument} from "./types";
import type {TocEntry} from "./tocUtils";
import {buildTocTree} from "./tocUtils";
import {buildTopicUrl} from "./contentUtils";
import classes from "./TableOfContents.module.css";
import {Link} from "react-router-dom";

interface TableOfContentsProps {
    document: DibDocument;
    activeSegmentId?: string;
    clickedId?: string | null;
    onClickId?: (id: string) => void;
    onNavigate?: (entry: TocEntry) => void;
}

export const TableOfContents = ({document: doc, activeSegmentId, clickedId, onClickId, onNavigate}: TableOfContentsProps) => {
    const {t} = useTranslation();
    const entries = useMemo(() => buildTocTree(doc), [doc]);
    const {topictypeId, name, topicId} = doc;

    if (entries.length === 0) return null;

    return (
        <nav className={classes.toc} aria-label={t("Title.LabelOfContents", {defaultValue: "Table of contents"})} data-click-override={clickedId ? "" : undefined}>
            <ul className={classes.list}>
                {entries.map((entry) => (
                    <TocItem key={entry.id} entry={entry} topictypeId={topictypeId} name={name} topicId={topicId} activeSegmentId={activeSegmentId} clickedId={clickedId} onClickId={onClickId} onNavigate={onNavigate} />
                ))}
            </ul>
        </nav>
    );
};

interface TocItemProps {
    entry: TocEntry;
    topictypeId: string;
    name: string;
    topicId: string;
    activeSegmentId?: string;
    clickedId?: string | null;
    onClickId?: (id: string) => void;
    onNavigate?: (entry: TocEntry) => void;
}

const TocItem = ({entry, topictypeId, name, topicId, activeSegmentId, clickedId, onClickId, onNavigate}: TocItemProps) => {
    let href = `#${entry.id}`;
    const isActive = clickedId
        ? clickedId === entry.id
        : entry.seg === "1" && entry.id === activeSegmentId;

    // data-current gives other stylesheets a stable hook for the active row.
    // Class names from this CSS module are hashed, so they cannot be
    // targeted from outside this file.
    let link = (
        <a href={href} data-current={isActive ? "true" : undefined} className={`${classes.link}${isActive ? ` ${classes.active}` : ""}`} onClick={(event) => {
            onClickId?.(entry.id);
            if (onNavigate) {
                event.preventDefault();
                onNavigate(entry);
            }
        }}>
            {entry.title}
        </a>
    );

    if (entry.seg === "1") {
        href = buildTopicUrl(topictypeId, name, topicId, entry.id) + `#${entry.id}`;
        link = (
            <Link to={href} data-current={isActive ? "true" : undefined} className={`${classes.link}${isActive ? ` ${classes.active}` : ""}`} onClick={(event) => {
                onClickId?.(entry.id);
                if (onNavigate) {
                    event.preventDefault();
                    onNavigate(entry);
                }
            }}>
                {entry.title}
            </Link>
        );
    }

    return (
        <li className={classes.item}>
            {link}
            {entry.children.length > 0 && (
                <div className={classes.collapse}>
                    <ul className={classes.children}>
                        {entry.children.map((child) => (
                            <TocItem
                                key={child.id}
                                entry={child}
                                topictypeId={topictypeId}
                                name={name}
                                topicId={topicId}
                                activeSegmentId={activeSegmentId}
                                clickedId={clickedId}
                                onClickId={onClickId}
                                onNavigate={onNavigate}
                            />
                        ))}
                    </ul>
                </div>
            )}
        </li>
    );
};
